import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";

import { Input, Select, SelectOption } from "@mwi/ui";
import {
  createDraftConversation,
  resetDraftConversation,
  updateDraftConversation,
} from "slices/messages";
import { useGetPatientsQuery } from "api";
import { RootState } from "store";

type Inputs = {
  newConversationTitle: string;
  newConversationPet?: string;
};

const PetSelector = ({
  control,
  register,
  onChange,
  options,
}): JSX.Element | null => {
  if (options && options?.length > 0)
    return (
      <div className="w-[310px]">
        <Select
          control={control}
          {...register("newConversationPet")}
          onChange={onChange}
          data-test-id="MessagesSection_Conversation_PetSelect-Input"
          name="MessagesSection_Conversation_PetSelect-Input"
          placeholder="Select a pet"
          size="large"
        >
          <SelectOption value="false">No pet</SelectOption>
          {options.map((option) => (
            <SelectOption key={option.id} value={option.id}>
              {option.name}
            </SelectOption>
          ))}
        </Select>
      </div>
    );

  return null;
};

const NewConversationHeader = (): JSX.Element => {
  const { register, watch, control } = useForm<Inputs>();
  const dispatch = useDispatch();

  const {
    data: patients,
    isFetching: isFetchingPatients,
    isError,
  } = useGetPatientsQuery();

  const { draftConversation } = useSelector((state: RootState) => ({
    draftConversation: state.messages.draftConversation,
  }));

  useEffect(() => {
    dispatch(createDraftConversation());
    return () => {
      dispatch(resetDraftConversation());
    };
  }, []);

  const updateDraftPet = (petId) => {
    if (draftConversation) {
      if (petId === "false") {
        const { pet: _, ...newSummary } = draftConversation.summary!;
        dispatch(
          updateDraftConversation({
            conversation: {
              summary: { ...newSummary },
            },
          }),
        );
      } else {
        const patient = petId
          ? patients?.find((p) => p.id == petId)
          : undefined;
        dispatch(
          updateDraftConversation({
            conversation: {
              summary: {
                ...draftConversation.summary!,
                ...(petId &&
                  patient && { pet: [{ id: petId, name: patient.name }] }),
                totalMessages: 0,
              },
            },
          }),
        );
      }
    }
  };

  const watchConversationTitle = watch("newConversationTitle");

  useEffect(() => {
    if (draftConversation) {
      dispatch(
        updateDraftConversation({
          conversation: {
            summary: {
              ...draftConversation.summary!,
              title: watchConversationTitle,
            },
          },
        }),
      );
    }
  }, [watchConversationTitle]);

  return (
    <div className="flex-grow-0 tablet:border-b tablet:border-grey-light pb-4 mx-4 tablet:mb-6">
      <Input
        className="my-4"
        {...register("newConversationTitle")}
        autoComplete="off"
        placeHolder="Conversation topic"
        data-test-id="NewConversationHeader_Title-input"
      />
      <div className="flex flex-row justify-between items-center gap-2">
        {!isFetchingPatients && (
          <PetSelector
            control={control}
            register={register}
            onChange={updateDraftPet}
            options={patients}
          />
        )}
      </div>
    </div>
  );
};

export default NewConversationHeader;
